import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Target, Eye, Heart, Users, Sparkle } from '@phosphor-icons/react';

export default function About() {
  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          <Link to="/">
            <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/30">
              <Sparkle weight="fill" className="w-4 h-4" />
              About MenoPath
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
              Bringing Clarity to the{' '}
              <span className="text-[#A78BFA]">Menopause Timeline</span>
            </h1>
            <p className="mt-6 text-xl text-white/90 max-w-2xl mx-auto">
              Through Science, Not Guesswork
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* The Problem Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="glass-card rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                The Challenge We're Solving
              </h2>
              <div className="prose prose-lg max-w-none text-[#374151] space-y-4">
                <p>
                  Menopause is one of the most universal—and yet least understood—health transitions 
                  in a woman's life. Despite affecting every woman, it has long been surrounded by 
                  uncertainty, inconsistent guidance, and oversimplified explanations.
                </p>
                <p>
                  Many women are told to <em>"wait and see,"</em> to track symptoms without context, 
                  or to accept changes without understanding <strong>why</strong> they are happening 
                  or <strong>what comes next</strong>.
                </p>
                <p>
                  For too long, answers have arrived late—often only after years of confusion, 
                  misattributed symptoms, or missed opportunities for early support. Lab results 
                  are frequently interpreted without age context, symptoms are treated in isolation, 
                  and the underlying biology driving the transition is rarely explained.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Our Solution Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-[#EDE9FE] to-[#D1FAE5] rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                Why MenoPath Exists
              </h2>
              <div className="prose prose-lg max-w-none text-[#374151] space-y-4">
                <p>
                  <strong className="text-[#5B21B6]">MenoPath was created to change that.</strong> By focusing on 
                  the biological signals that truly matter, MenoPath brings clarity to the menopause 
                  timeline—earlier, more accurately, and with actionable insight.
                </p>
                <p>
                  We help women and clinicians move from <span className="text-[#5B21B6] font-semibold">uncertainty 
                  to understanding</span>, and from <span className="text-[#059669] font-semibold">reaction to 
                  informed choice</span>.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Mission Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="glass-card rounded-3xl p-8 md:p-12 border-2 border-[#5B21B6]/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" weight="duotone" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit']">
                  Our Mission
                </h2>
              </div>
              <div className="prose prose-lg max-w-none text-[#374151] space-y-4">
                <p className="text-xl leading-relaxed">
                  Our mission is to give women and clinicians <strong className="text-[#5B21B6]">earlier, 
                  clearer, and more actionable insight</strong> into the menopause transition, grounded 
                  in real biology—not assumptions, averages, or guesswork.
                </p>
                <p>
                  For decades, menopause care has relied heavily on symptoms and retrospective diagnosis, 
                  often leaving women without meaningful guidance until the transition is well underway. 
                  <strong> We believe women deserve better:</strong> insight that arrives sooner, 
                  explanations that make sense, and tools that support informed decisions rather than 
                  reactive care.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Our Belief Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="glass-card rounded-3xl p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#059669] to-[#10B981] flex items-center justify-center">
                  <Eye className="w-7 h-7 text-white" weight="duotone" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit']">
                  Our Belief
                </h2>
              </div>
              <div className="prose prose-lg max-w-none text-[#374151]">
                <p>
                  We believe menopause timing is <strong>predictable</strong> when you focus on the 
                  right biological signals and interpret them in the context of age, individual history, 
                  and lived experience.
                </p>
                <p>
                  By combining validated hormone biomarkers with thoughtful modeling and clear visualization, 
                  MenoPath helps shift menopause care from <span className="text-[#DC2626]">uncertainty</span> to 
                  <span className="text-[#059669]"> understanding</span>—and from late recognition to 
                  <span className="text-[#5B21B6]"> proactive support</span>.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Values Grid */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-8 text-center">
              What Drives Us
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-card rounded-2xl p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-full bg-[#EDE9FE] flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-[#5B21B6]" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-[#1E1B4B] font-['Outfit'] mb-2">Precision</h3>
                <p className="text-sm text-[#6B7280]">
                  Science-backed predictions using validated biomarkers and AI analysis
                </p>
              </div>
              
              <div className="glass-card rounded-2xl p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-7 h-7 text-[#059669]" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-[#1E1B4B] font-['Outfit'] mb-2">Empathy</h3>
                <p className="text-sm text-[#6B7280]">
                  Understanding the emotional journey and providing supportive guidance
                </p>
              </div>
              
              <div className="glass-card rounded-2xl p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-full bg-[#FEF3C7] flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-[#92400E]" weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-[#1E1B4B] font-['Outfit'] mb-2">Community</h3>
                <p className="text-sm text-[#6B7280]">
                  Building a supportive network of women navigating this journey together
                </p>
              </div>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
                Ready to Take Control?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Join thousands of women who have moved from confusion to clarity with MenoPath.
              </p>
              <Link to="/register">
                <button className="bg-white text-[#5B21B6] font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-colors shadow-lg">
                  Start Your Journey Today
                </button>
              </Link>
            </div>
          </motion.section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1E1B4B] py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-6">
            <Link to="/about" className="text-white/70 hover:text-white transition-colors text-sm">About</Link>
            <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
